import {Component, Inject, Signal} from '@angular/core';
import {File, Folder, Mutation, ProcessStateEnum} from '../../../types-repository';
import {CREATE_EDITING_WORKSPACE} from '../../../queries-repository';
import {Apollo} from 'apollo-angular';
import {DIALOG_DATA, DialogRef} from '@angular/cdk/dialog';
import {switchMap, tap} from 'rxjs';
import {skipWhile} from 'rxjs/operators';
import {ProcessService} from '../../../services/process.service';
import {FormBuilder, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {DoButtonComponent} from '../../do-button/do-button.component';
import {MessageFieldComponent} from '../../do-button/message-field.component';
import {timer} from 'rxjs';
import {UserProfileService} from '../../../services/user-profile.service';
import {ContextService} from '../../../services/context.service';

@Component({
  selector: 'app-remote-edit-dialog',
  templateUrl: './remote-edit-dialog.component.html',
  styleUrls: ['./remote-edit-dialog.component.scss'],
  imports: [
    ReactiveFormsModule,
    DoButtonComponent,
    MessageFieldComponent
  ]
})
export class RemoteEditDialogComponent {
  form: FormGroup;
  staff: Signal<boolean>;
  items: (File | Folder)[];

  availabilityZones = [
    {value: 'eu-west-1a', label: 'Dublin, Ireland (eu-west-1a)'},
    {value: 'eu-central-1a', label: 'Frankfurt, Germany (eu-central-1a)'},
    {value: 'eu-south-2a', label: 'Madrid, Spain (eu-south-2a)'},
    {value: 'us-east-1a', label: 'Virginia, USA (us-east-1a)'},
    {value: 'us-west-2a', label: 'Oregon, USA (us-west-2a)'},
    {value: 'af-south-1a', label: 'Cape Town, South Africa (af-south-1a)'},
  ];

  constructor(
    public dialogRef: DialogRef<RemoteEditDialogComponent>,
    @Inject(DIALOG_DATA) public data: {
      items: (File | Folder)[]
    },
    private apollo: Apollo,
    private processService: ProcessService,
    private fb: FormBuilder,
    userProfileService: UserProfileService,
    private contextService: ContextService
  ) {
    this.items = this.data.items;
    this.form = this.fb.group({
      availabilityZone: ['eu-west-1a', [Validators.required]],
      mountPoint: ['', [
        Validators.required,
        Validators.maxLength(32),
        Validators.pattern(/^[^\\\/:*?"<>|]+$/)
      ]],
      message: ['']
    });
    this.staff = userProfileService.staff;
  }

  createEditingWorkspace(button: DoButtonComponent) {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      button.info('Please correct the highlighted fields.', {severity: 'cl-alert-warning', duration: 5000});
      return;
    }

    const contentIds = this.items.map(item => item.id);
    const contents_str = contentIds.join(', ');
    console.log(`Starting remote edit for: ${contents_str}`);

    const availabilityZone = this.form.get('availabilityZone')?.value;
    const mountPoint = this.form.get('mountPoint')?.value;
    const message = this.form.get('message')?.value || null;

    this.apollo.mutate<Mutation>({
      mutation: CREATE_EDITING_WORKSPACE,
      variables: {
        pool: this.contextService.organization().legacy_name,
        availabilityZone,
        contents: contentIds,
        mountPoint,
        message,
      },
      fetchPolicy: 'network-only'
    }).pipe(
      tap(res => {
        button.info('Starting remote edit session...', {duration: -1});
        // Close dialog immediately once the process is kicked off
        this.dialogRef.close();
      }),
      switchMap(r => this.processService.observeProcess(r.data.createEditingWorkspace,
        {
          successMessage: `Successfully created editing workspace for ${contents_str}.`,
          failureMessage: `Failed to create editing workspace for ${contents_str}.`,
          link: window.location.href
        }
      )),
      skipWhile(process => {
        return process.state === ProcessStateEnum.InProgress
      })
    ).subscribe({
      next: (process) => {
        if (process.state === ProcessStateEnum.Success) {
          button.done(process.message || `Successfully started remote edit session.`);
          timer(1000).subscribe(() => this.dialogRef.close());
        }
        if (process.state === ProcessStateEnum.Failed) {
          button.info(process.message || `Failed to start remote edit session.`, {severity: 'cl-alert-danger', duration: -1});
        }
      },
      error: (err) => {
        button.info(`Failed to start remote edit session: ${err.message || err}`, {severity: 'cl-alert-danger', duration: -1});
      }
    });
  }
}
